import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import DataGrid from '../../../components/DataGrid/DataGrid';
import { hoursColDef } from '../../../utils/reportingUtils/datagridUtils';
import { useGetApproachingACAHourlyThresholdGridData } from '../../requests/queries';
import { getMuiFormattedFilterRequest, getOptionLabels } from '../../../utils/reportingUtils/filterUtils';
import { ACAThresholdFilters } from '../ACAThresholdReportFilters';
import { ActualHoursWorked } from '../../types';
import useCfcOrg from '../../../utils/hooks/useCfcOrg';
import useSiteColumnVisibility from '../../../hooks/useSiteColumnVisibility';

export interface ACAHourlyThresholdDataGridProps {
  filters?: ACAThresholdFilters;
  hoursWorked: Record<string, ActualHoursWorked>;
}

function ACAHourlyThresholdDataGrid({ filters, hoursWorked }: Readonly<ACAHourlyThresholdDataGridProps>) {
  const { t } = useTranslation();

  const { isCfcOrg } = useCfcOrg();

  const { columnVisibilityModel, onColumnVisibilityModelChange } = useSiteColumnVisibility(filters, 'siteName', {
    employeePosId: isCfcOrg,
  });

  const {
    getApproachingACAHourlyThresholdGridData,
    setGetApproachingACAHourlyThresholdGridDataRequest,
    isGetApproachingACAHourlyThresholdGridDataLoading,
  } = useGetApproachingACAHourlyThresholdGridData();

  const acaHourlyCommonProp = { flex: 1, minWidth: 125 };
  const acaThresholdHourlyColumns: GridColDef[] = [
    {
      field: 'siteName',
      headerName: t('labor.approachingACA.site'),
      ...acaHourlyCommonProp,
    },
    {
      field: 'employeeName',
      headerName: t('labor.approachingACA.name'),
      ...acaHourlyCommonProp,
    },
    {
      field: 'employeePosId',
      headerName: t('labor.approachingACA.employeePosId'),
      ...acaHourlyCommonProp,
      minWidth: 185,
    },
    {
      field: 'jobType',
      headerName: t('labor.employeeType'),
      ...acaHourlyCommonProp,
    },
    {
      field: 'employeeStatus',
      headerName: t('labor.approachingACA.hourly.gridColumns.employeeStatus'),
      ...acaHourlyCommonProp,
      minWidth: 150,
    },
    {
      field: 'actualHours',
      headerName: t('labor.approachingACA.hourly.gridColumns.actualHours'),
      ...acaHourlyCommonProp,
      ...hoursColDef,
    },
    {
      field: 'remainingHours',
      headerName: t('labor.approachingACA.hourly.gridColumns.remainingHours'),
      ...acaHourlyCommonProp,
      ...hoursColDef,
      minWidth: 150,
    },
    {
      field: 'expectedTotalHours',
      headerName: t('labor.approachingACA.hourly.gridColumns.expectedTotalHours'),
      ...acaHourlyCommonProp,
      ...hoursColDef,
      minWidth: 185,
    },
  ];

  useEffect(() => {
    if (filters) {
      const { sites, jobType, employeeStatus, hourlyThresholds, datePickerNav } = filters;

      const weekStart = datePickerNav.locale('schedule-offset').startOf('week').format('YYYY-MM-DD');
      const weekEnd = datePickerNav.locale('schedule-offset').endOf('week').format('YYYY-MM-DD');
      const { validRequest, request } = getMuiFormattedFilterRequest(weekStart, weekEnd, sites);

      const thresholds: ActualHoursWorked | undefined =
        hourlyThresholds.length > 0 ? hoursWorked[hourlyThresholds[0].label] : undefined;

      if (validRequest) {
        setGetApproachingACAHourlyThresholdGridDataRequest({
          ...request,
          jobType: getOptionLabels(jobType),
          employeeStatus: getOptionLabels(employeeStatus),
          ...(thresholds && { hourThresholdLowerRange: thresholds.lower, hourThresholdHigherRange: thresholds.higher }),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, setGetApproachingACAHourlyThresholdGridDataRequest, isCfcOrg]);

  return (
    <DataGrid
      columns={acaThresholdHourlyColumns}
      rows={getApproachingACAHourlyThresholdGridData?.results || []}
      pageSizeOptions={[5, 10, 25]}
      loading={isGetApproachingACAHourlyThresholdGridDataLoading}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={onColumnVisibilityModelChange}
      hideFromColumnSelector={isCfcOrg ? ['siteName'] : ['siteName', 'employeePosId']}
    />
  );
}

export default ACAHourlyThresholdDataGrid;
